import { PlanTrailActionContext } from "./types";
import graphClient from "../../../../../utils/graphql/graph_client";
import { CreateHikeWithPlannedTrails, UpdateHikeWithPlannedTrails } from "../../../../../utils/graphql/hikes-graph";
import moment from 'moment';
import 'moment-timezone';
import { Modal } from "bootstrap";
import { forceSync, hasFirebaseUser } from "../../../../../utils/firebase/authSync";

export const saveShareActions = {
  async save({ dispatch }: PlanTrailActionContext, shouldRedirect: boolean = true) {
    // Always save as hike now
    return dispatch('saveAsHike', shouldRedirect);
  },

  async saveAsHike({ state, getters }: PlanTrailActionContext, shouldRedirect: boolean = true) {
    // Don't continue if photos are uploading
    if (state.photosToUpload.length > 0) {
      return alert("Please wait for photos to upload before saving");
    }

    state.loading = true;

    try {
      // Check authentication before attempting to save
      if (!hasFirebaseUser()) {
        // No Firebase auth - show sign-in modal
        state.loading = false;
        const signupDialog = document.getElementById('signup-dialog');
        if (signupDialog) {
          const modal = new Modal(signupDialog);
          modal.show();
        }
        return;
      }

      // Sync with Rails before saving to ensure the session is valid
      // This handles the case where the Rails session expired during a long editing session
      // Note: The graphClient will automatically include the Firebase token on every request
      console.log('[Save] Syncing Firebase auth with Rails before save...');
      const synced = await forceSync();
      if (!synced) {
        state.loading = false;
        // Show sign-in modal if sync failed
        const signupDialog = document.getElementById('signup-dialog');
        if (signupDialog) {
          const modal = new Modal(signupDialog);
          modal.show();
        }
        return;
      }

      // Check if we have any routes
      const routesArray = getters['routesArray'];
      if (routesArray.length === 0) {
        state.loading = false;
        return alert("Please add at least one route to save as a hike");
      }

      // Check if all routes have at least 2 markers (need 2 points to make a route)
      const incompleteRoutes = routesArray.filter((route: any) => route.markers.length < 2);
      if (incompleteRoutes.length > 0) {
        state.loading = false;
        return alert("All routes must have at least 2 markers to create a valid route");
      }

      if (getters['isTransportOnlyPlan']) {
        state.loading = false;
        return alert('Transport-only plans cannot be saved. Please add a hiking segment before saving your plan.');
      }

      // Validate that the plan has at least one main route (route type)
      if (!getters['hasMainRoute']) {
        state.loading = false;
        return alert('Your plan must contain at least one route type segment. Please add a route segment before saving.');
      }

      // Validate that each hike day has at least one main route
      const dayValidation = getters['validateHikeDaysHaveMainRoute'];
      if (!dayValidation.valid) {
        state.loading = false;
        const dayList = dayValidation.invalidDays.map((d: number) => `Day ${d}`).join(', ');
        return alert(`Each hike day must contain at least one route or transport segment. The following days only have alt or spur segments: ${dayList}`);
      }

      let titleToSet = state.title || `Hike - ${new Date().toLocaleDateString()}`;

      const featureTags = state.selectedFeatureTags.filter((tag: string) => !!tag);
      const activityTags = state.selectedActivityTags.filter((tag: string) => !!tag);

      // Check if we're updating an existing hike
      if (state.isHikeEdit && state.hikeId) {
        const plannedTrailsAttributes = getters['getPlannedTrailsAttributesForHike'];
        console.log('[SAVE] Updating existing hike - hikeId:', state.hikeId, 'isHikeEdit:', state.isHikeEdit);
        console.log('[SAVE] plannedTrailsAttributes:', plannedTrailsAttributes);

        const variables = {
          input: {
            id: state.hikeId,
            name: titleToSet,
            description: state.description || '',
            publiclyVisible: state.public,
            difficulty: getters['difficulty'],
            distanceMeters: Math.round(getters['totalDistanceExcludingTransport']),
            elevationGainMeters: Math.round(getters['totalAscent']),
            elevationLossMeters: Math.round(getters['totalElevationLoss']),
            plannedTrailsAttributes: plannedTrailsAttributes,
            featureTags: featureTags,
            activityTags: activityTags,
            plannedToHikeAt: state.plannedToHikeOn || null,
          }
        };

        // Submit for official review if requested. Only set when the box is
        // ticked so a normal edit doesn't clear an already-pending request.
        if (state.submitForOfficialApproval) {
          variables.input.officialHikeApprovalRequestedAt = new Date().toISOString();
        }

        // Add trails@hiiker.co specific options
        if (state.makeOfficial) {
          variables.input.makeOfficial = true;
        }
        if (state.generateAiDescription) {
          variables.input.generateAiDescription = true;
        }

        // Add images if any
        if (getters.getPtv2ImageAttributes.length > 0) {
          variables.input.imagesAttributes = getters.getPtv2ImageAttributes;
        }

        // Add hike days if any
        const hikeDaysAttributes = getters['getHikeDaysAttributes'];
        if (hikeDaysAttributes && hikeDaysAttributes.length > 0) {
          variables.input.hikeDaysAttributes = hikeDaysAttributes;
        }

        // Add hike highlights if any
        const hikeHighlightsAttributes = getters['getHikeHighlightsAttributes'];
        if (hikeHighlightsAttributes && hikeHighlightsAttributes.length > 0) {
          variables.input.hikeHighlightsAttributes = hikeHighlightsAttributes;
        }

        const response = await graphClient.request(
          UpdateHikeWithPlannedTrails,
          variables
        );

        if (response.updateHike?.errors?.length > 0) {
          state.loading = false;
          const errors = response.updateHike.errors.map((e: any) =>
            `${e.fieldName}: ${e.errors.join(', ')}`
          ).join('\n');
          return alert(`Errors updating hike:\n${errors}`);
        }

        if (response.updateHike?.hike?.id) {
          localStorage.removeItem('store');
          if (shouldRedirect) {
            // Redirect to the user's hikes page
            window.location.href = '/user/hikes';
          } else {
            state.loading = false;
            // Show success message
            alert(`Hike "${titleToSet}" updated successfully with ${routesArray.length} route(s)`);
          }
        } else {
          state.loading = false;
          return alert("Unknown error occurred while updating hike");
        }
      } else {
        // Create new hike
        const variables = {
          input: {
            name: titleToSet,
            description: state.description || '',
            publiclyVisible: state.public,
            difficulty: getters['difficulty'],
            distanceMeters: Math.round(getters['totalDistanceExcludingTransport']),
            elevationGainMeters: Math.round(getters['totalAscent']),
            elevationLossMeters: Math.round(getters['totalElevationLoss']),
            plannedTrailsAttributes: getters['getPlannedTrailsAttributesForHike'],
            featureTags: featureTags,
            activityTags: activityTags,
            plannedToHikeAt: state.plannedToHikeOn || null,
            officialHikeApprovalRequestedAt: state.submitForOfficialApproval ? new Date().toISOString() : null,
          }
        };

        // Add trail data ID if set (for trails@hiiker.co creating from trail data)
        if (state.trailDataId) {
          variables.input.trailDataId = state.trailDataId.toString();
        }

        // Link back to the hike this was copied from ("Edit a Copy") so the
        // backend can propagate comments to the original hike.
        if (state.originalHikeId) {
          variables.input.originalHikeId = state.originalHikeId.toString();
        }

        // Add trails@hiiker.co specific options
        if (state.makeOfficial) {
          variables.input.makeOfficial = true;
        }
        if (state.generateAiDescription) {
          variables.input.generateAiDescription = true;
        }

        // Add images if any
        if (getters.getPtv2ImageAttributes.length > 0) {
          variables.input.imagesAttributes = getters.getPtv2ImageAttributes;
        }

        // Add hike days if any
        const hikeDaysAttributes = getters['getHikeDaysAttributes'];
        if (hikeDaysAttributes && hikeDaysAttributes.length > 0) {
          variables.input.hikeDaysAttributes = hikeDaysAttributes;
        }
        
        // Add hike highlights if any
        const hikeHighlightsAttributes = getters['getHikeHighlightsAttributes'];
        if (hikeHighlightsAttributes && hikeHighlightsAttributes.length > 0) {
          variables.input.hikeHighlightsAttributes = hikeHighlightsAttributes;
        }

        const response = await graphClient.request(
          CreateHikeWithPlannedTrails,
          variables
        );

        if (response.createHike?.errors?.length > 0) {
          state.loading = false;
          const errors = response.createHike.errors.map((e: any) =>
            `${e.fieldName}: ${e.errors.join(', ')}`
          ).join('\n');
          console.error('Errors creating hike:', errors);
          return alert(`Errors creating hike:\n${errors}`);
        }

        if (response.createHike?.hike?.id) {
          localStorage.removeItem('store');
          if (shouldRedirect) {
            // Redirect to the user's hikes page
            window.location.href = '/user/hikes';
          } else {
            state.loading = false;
            // Show success message
            alert(`Hike "${titleToSet}" created successfully with ${routesArray.length} route(s)`);
          }
        } else {
          state.loading = false;
          return alert("Unknown error occurred while creating hike");
        }
      }
    } catch (error) {
      state.loading = false;
      console.error('Error saving hike:', error);
      return alert(`Error saving hike: ${error.message || error}`);
    }
  },

  async shareRoute({ state, dispatch }: PlanTrailActionContext) {
    state.loading = true;
    await dispatch('save', false);
    if (window.plannedTrail && window.plannedTrail.shareableUrl) {
      state.shareableUrl = window.plannedTrail.shareableUrl;
      // @ts-ignore
      const element = document.getElementById('share-dialog');
      const modal = new Modal(element);
      modal.show();
    }
    state.loading = false;
  },

  setPlannedToHikeOn({ state }: PlanTrailActionContext, value: string) {
    // Your local time value
    const localTimeValue = value;

    // Parse the local time value
    const localMoment = moment(localTimeValue);

    // Convert the local time value to UTC
    const utcMoment = localMoment.utc();

    // Format the UTC time as a string (e.g., "YYYY-MM-DDTHH:mm:ss[Z]")
    const utcString = utcMoment.format("YYYY-MM-DDTHH:mm:ss[Z]");

    state.plannedToHikeOn = utcString;
  }
};
